/**
 * Una semana: cada día, una columna por trabajador.
 * Pinchar un cuadro marca a esa persona. Arrastrar los bordes le cambia el
 * horario —y la ficha de abajo se entera, porque es el mismo plan.
 */
import { useMemo, useRef, useState, type PointerEvent as PE } from 'react'

import { bajaDe, iniciales } from '../../lib/horarios-baja'
import { bandasDelDia, diasConAlguien, horarioDeLaSemana, jornadaCobrada } from '../../lib/horarios-cobertura'
import { colorConAlfa, colorDeProfesional } from '../../lib/horarios-colores'
import {
    ajustarExtremo,
    franjaDeTramos,
    partirTramos,
    tramosDeFranja,
    type Plan,
} from '../../lib/horarios-tramos'
import { aHhmm, diaSemanaDe, horasDe } from '../calendario-geometria'
import type { PeriodoCierre } from '../calendario-cerrados'

const PX = 28
const DIAS_CORTO = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'] as const

export type SeleccionHorario = { profesionalId: number; dia: string; tramo: number } | null

export function VistaCoberturaSemanal({
    profesionales,
    enPantalla,
    vacaciones,
    dias,
    ocultarCerrados,
    onOcultarCerrados,
    onToggleVisible,
    seleccionado,
    onSeleccionar,
    onCambiarPlan,
    onPreguntarAlcance,
}: {
    profesionales: { id: number; nombre: string; plan: Plan; color: string }[]
    enPantalla: { id: number; nombre: string; plan: Plan; color: string }[]
    vacaciones: PeriodoCierre[]
    dias: string[]
    ocultarCerrados: boolean
    onOcultarCerrados: (v: boolean) => void
    onToggleVisible: (id: number) => void
    seleccionado: SeleccionHorario
    onSeleccionar: (s: SeleccionHorario) => void
    onCambiarPlan: (profesionalId: number, plan: Plan) => void
    onPreguntarAlcance: (profesionalId: number, dia: keyof Plan, punto: { x: number; y: number }) => void
}) {
    const visibles = useMemo(
        () => (ocultarCerrados ? diasConAlguien(enPantalla, dias, vacaciones) : dias),
        [ocultarCerrados, enPantalla, dias, vacaciones],
    )
    const horario = horarioDeLaSemana(enPantalla, visibles)
    const alto = ((horario.hastaMin - horario.desdeMin) / 60) * PX
    const horas = horasDe(horario)
    const indiceDia = new Map(dias.map((d, i) => [d, i]))
    const elegido = enPantalla.find((p) => p.id === seleccionado?.profesionalId)
    const tramosElegido =
        elegido && seleccionado ? tramosDeFranja(elegido.plan[diaSemanaDe(seleccionado.dia)]) : []
    const n = Math.max(1, enPantalla.length)

    function partirSeleccionado() {
        if (!elegido || !seleccionado) return
        const clave = diaSemanaDe(seleccionado.dia)
        const partidos = partirTramos(tramosDeFranja(elegido.plan[clave]))
        if (!partidos) return
        onCambiarPlan(elegido.id, { ...elegido.plan, [clave]: franjaDeTramos(partidos) })
        onSeleccionar({ ...seleccionado, tramo: 0 })
        onPreguntarAlcance(elegido.id, clave, { x: window.innerWidth / 2, y: 180 })
    }

    return (
        <section className="space-y-2 rounded-lg border border-neutral-800 bg-neutral-950/50 p-2.5">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-medium text-neutral-100">La semana</h3>
                    <p className="text-xs text-neutral-500">
                        Cada uno en su columna. Apaga a quien no quieras ver. Pincha un cuadro
                        para marcar; arrastra el borde para cambiarle el horario.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {seleccionado && tramosElegido.length === 1 && (
                        <button
                            type="button"
                            onClick={partirSeleccionado}
                            className="min-h-11 rounded border border-neutral-700 px-2 py-0.5 text-[11px] text-neutral-300 hover:bg-neutral-800 md:min-h-0"
                        >
                            Partir horario
                        </button>
                    )}
                    <label className="flex items-center gap-2 text-xs text-neutral-400">
                        <input
                            type="checkbox"
                            checked={ocultarCerrados}
                            onChange={(e) => onOcultarCerrados(e.target.checked)}
                            className="h-3.5 w-3.5 rounded border-neutral-700 bg-neutral-900"
                        />
                        Ocultar días sin nadie
                    </label>
                </div>
            </header>

            <ul className="flex flex-wrap gap-x-3 gap-y-1">
                {profesionales.map((p) => {
                    const seVe = enPantalla.some((x) => x.id === p.id)
                    const activo = seleccionado?.profesionalId === p.id
                    return (
                        <li key={p.id} className="flex items-center gap-0.5">
                            <button
                                type="button"
                                aria-pressed={seVe}
                                aria-label={seVe ? `Ocultar a ${p.nombre}` : `Mostrar a ${p.nombre}`}
                                onClick={() => onToggleVisible(p.id)}
                                className="rounded p-1 hover:bg-neutral-800"
                            >
                                <span
                                    className="block h-2.5 w-2.5 rounded-sm"
                                    style={{ background: seVe ? p.color : colorConAlfa(p.color, 0.25) }}
                                    aria-hidden
                                />
                            </button>
                            <button
                                type="button"
                                disabled={!seVe}
                                onClick={() =>
                                    onSeleccionar(
                                        activo
                                            ? null
                                            : { profesionalId: p.id, dia: visibles[0] ?? dias[0]!, tramo: 0 },
                                    )
                                }
                                className={
                                    'rounded px-1 py-0.5 text-xs ' +
                                    (seVe
                                        ? activo
                                            ? 'bg-neutral-800 text-neutral-100'
                                            : 'text-neutral-300 hover:text-neutral-100'
                                        : 'text-neutral-600 line-through')
                                }
                            >
                                {p.nombre}
                            </button>
                        </li>
                    )
                })}
            </ul>

            {enPantalla.length === 0 ? (
                <p className="py-6 text-center text-xs text-neutral-500">
                    No hay nadie visible. Enciende a alguien arriba.
                </p>
            ) : (
                <div className="overflow-x-auto overflow-y-hidden">
                    <div
                        className="grid w-max min-w-full gap-px"
                        style={{
                            gridTemplateColumns: `2.75rem repeat(${visibles.length}, minmax(${Math.max(5.5, n * 2.5)}rem, 1fr))`,
                        }}
                    >
                        <div className="h-10" />
                        {visibles.map((dia, di) => {
                            const i = indiceDia.get(dia) ?? 0
                            return (
                                <div
                                    key={`h-${dia}`}
                                    className={
                                        'flex h-10 flex-col justify-end pb-0.5 text-center ' +
                                        (di > 0 ? 'shadow-[-2px_0_0_0_#525252]' : '')
                                    }
                                >
                                    <p className="text-[11px] font-medium text-neutral-300">{DIAS_CORTO[i]}</p>
                                    <p className="text-[10px] text-neutral-500">{Number(dia.slice(8, 10))}</p>
                                </div>
                            )
                        })}

                        <div className="relative h-5" />
                        {visibles.map((dia, di) => (
                            <div
                                key={`i-${dia}`}
                                className={
                                    'grid h-5 gap-px ' + (di > 0 ? 'shadow-[-2px_0_0_0_#525252]' : '')
                                }
                                style={{ gridTemplateColumns: `repeat(${n}, minmax(2.15rem, 1fr))` }}
                            >
                                {enPantalla.map((p) => (
                                    <p
                                        key={p.id}
                                        className="truncate text-center text-[9px] font-medium"
                                        style={{ color: p.color }}
                                        title={p.nombre}
                                    >
                                        {iniciales(p.nombre)}
                                    </p>
                                ))}
                            </div>
                        ))}

                        <div className="relative" style={{ height: alto }}>
                            {horas.map((m) => (
                                <div
                                    key={m}
                                    className="absolute right-1 -translate-y-1/2 text-[10px] tabular-nums text-neutral-500"
                                    style={{ top: ((m - horario.desdeMin) / 60) * PX }}
                                >
                                    {aHhmm(m)}
                                </div>
                            ))}
                        </div>

                        {visibles.map((dia, di) => (
                            <div
                                key={dia}
                                className={'grid gap-px ' + (di > 0 ? 'shadow-[-2px_0_0_0_#525252]' : '')}
                                style={{ gridTemplateColumns: `repeat(${n}, minmax(2.15rem, 1fr))` }}
                            >
                                {enPantalla.map((p) => (
                                    <ColumnaPersona
                                        key={p.id}
                                        dia={dia}
                                        profesional={p}
                                        vacaciones={vacaciones}
                                        desde={horario.desdeMin}
                                        hasta={horario.hastaMin}
                                        alto={alto}
                                        horas={horas}
                                        seleccionado={seleccionado}
                                        onSeleccionar={onSeleccionar}
                                        onCambiarPlan={onCambiarPlan}
                                        onPreguntarAlcance={onPreguntarAlcance}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    )
}

function ColumnaPersona({
    dia,
    profesional,
    vacaciones,
    desde,
    hasta,
    alto,
    horas,
    seleccionado,
    onSeleccionar,
    onCambiarPlan,
    onPreguntarAlcance,
}: {
    dia: string
    profesional: { id: number; nombre: string; plan: Plan; color: string }
    vacaciones: PeriodoCierre[]
    desde: number
    hasta: number
    alto: number
    horas: number[]
    seleccionado: SeleccionHorario
    onSeleccionar: (s: SeleccionHorario) => void
    onCambiarPlan: (profesionalId: number, plan: Plan) => void
    onPreguntarAlcance: (profesionalId: number, dia: keyof Plan, punto: { x: number; y: number }) => void
}) {
    const bandas = bandasDelDia([profesional], dia, vacaciones)
    const cobrada = jornadaCobrada(profesional.plan, profesional.id, dia, vacaciones)
    const deBaja =
        cobrada.length === 0 && Boolean(bajaDe(vacaciones, profesional.id, dia))
    const span = Math.max(1, hasta - desde)
    const color = profesional.color ?? colorDeProfesional(profesional.id)
    const [arrastre, setArrastre] = useState<{
        tramo: number
        extremo: 'start' | 'end'
    } | null>(null)
    const columna = useRef<HTMLDivElement>(null)

    function minutoDe(clientY: number): number {
        const caja = columna.current?.getBoundingClientRect()
        if (!caja) return desde
        const y = clientY - caja.top
        return desde + (y / alto) * span
    }

    function aplicar(tramo: number, extremo: 'start' | 'end', minutos: number) {
        const clave = diaSemanaDe(dia)
        const next = ajustarExtremo(tramosDeFranja(profesional.plan[clave]), tramo, extremo, minutos)
        onCambiarPlan(profesional.id, { ...profesional.plan, [clave]: franjaDeTramos(next) })
    }

    return (
        <div
            ref={columna}
            className="relative min-w-0 overflow-hidden rounded-sm bg-neutral-900/90"
            style={{ height: alto }}
            aria-label={`${profesional.nombre} el ${dia}`}
            onPointerMove={(e) => {
                if (!arrastre) return
                aplicar(arrastre.tramo, arrastre.extremo, minutoDe(e.clientY))
            }}
            onPointerUp={(e) => {
                if (arrastre) {
                    onPreguntarAlcance(profesional.id, diaSemanaDe(dia), {
                        x: e.clientX,
                        y: e.clientY,
                    })
                }
                setArrastre(null)
            }}
            onPointerCancel={() => setArrastre(null)}
        >
            {horas.map((m) => (
                <div
                    key={m}
                    className="absolute inset-x-0 border-t border-neutral-800"
                    style={{ top: ((m - desde) / 60) * PX }}
                />
            ))}

            {deBaja && (
                <div
                    className="absolute inset-0 opacity-40"
                    style={{
                        background: `repeating-linear-gradient(-45deg, ${colorConAlfa(color, 0.35)} 0 4px, transparent 4px 8px)`,
                    }}
                    aria-hidden
                />
            )}

            {bandas.map((b, i) => {
                const esPersona = seleccionado?.profesionalId === profesional.id
                const esCuadro =
                    esPersona && seleccionado?.dia === dia && seleccionado.tramo === b.tramo
                const alfa = !seleccionado ? 0.55 : esCuadro ? 0.9 : esPersona ? 0.7 : 0.35
                return (
                    <div
                        key={`${b.desdeMin}-${i}`}
                        role="button"
                        tabIndex={0}
                        className="absolute inset-x-0.5 rounded-[3px]"
                        style={{
                            top: ((b.desdeMin - desde) / span) * alto + 1,
                            height: Math.max(8, ((b.hastaMin - b.desdeMin) / span) * alto - 2),
                            background: colorConAlfa(color, alfa),
                            border: `1px solid ${colorConAlfa(color, esCuadro ? 1 : 0.7)}`,
                            boxShadow: esCuadro ? `0 0 0 2px ${colorConAlfa(color, 0.55)}` : undefined,
                            zIndex: esCuadro ? 20 : 1,
                            cursor: 'pointer',
                        }}
                        title={`${profesional.nombre}: ${aHhmm(b.desdeMin)}–${aHhmm(b.hastaMin)}`}
                        onClick={() =>
                            onSeleccionar({ profesionalId: profesional.id, dia, tramo: b.tramo })
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onSeleccionar({ profesionalId: profesional.id, dia, tramo: b.tramo })
                            }
                        }}
                    >
                        {esCuadro && (
                            <>
                                <Asa
                                    donde="start"
                                    onPointerDown={(e) => {
                                        e.stopPropagation()
                                        columna.current?.setPointerCapture(e.pointerId)
                                        setArrastre({ tramo: b.tramo, extremo: 'start' })
                                    }}
                                />
                                <Asa
                                    donde="end"
                                    onPointerDown={(e) => {
                                        e.stopPropagation()
                                        columna.current?.setPointerCapture(e.pointerId)
                                        setArrastre({ tramo: b.tramo, extremo: 'end' })
                                    }}
                                />
                            </>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

function Asa({
    donde,
    onPointerDown,
}: {
    donde: 'start' | 'end'
    onPointerDown: (e: PE<HTMLDivElement>) => void
}) {
    return (
        <div
            className="absolute inset-x-1 h-2 cursor-ns-resize rounded-sm bg-white/70"
            style={{ [donde === 'start' ? 'top' : 'bottom']: 0 }}
            onPointerDown={onPointerDown}
            onClick={(e) => e.stopPropagation()}
        />
    )
}
